// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

function getDefaultHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

// Type Definitions
export interface Process {
  id: string;
  name: string;
  description?: string;
  businessArea?: string; // Department/business unit
  processOwner?: string; // Process owner name
  criticality?: 'critical' | 'high' | 'medium' | 'low';
  status?: 'active' | 'inactive' | 'under-review';
  
  // Connections to other entities
  suppliers?: string[]; // Supplier IDs/names involved in process
  systems?: string[]; // Systems used to execute the process
  criticalPeople?: string[]; // Staff IDs/names critical to process
  criticalSkills?: string[]; // Skill IDs/names required (if not specific people)
  customers?: string[]; // Customer IDs/names that leverage the process
  
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ProcessFilters {
  status?: string[];
  businessArea?: string[];
  criticality?: string[];
  search?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Get all processes with optional filtering
 */
export async function getAllProcesses(
  filters?: ProcessFilters
): Promise<ApiResponse<{ processes: Process[] }>> {
  try {
    let url = buildApiUrl('/processes');
    
    // Add query parameters for filters if provided
    if (filters) {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status.join(','));
      if (filters.businessArea) params.append('businessArea', filters.businessArea.join(','));
      if (filters.criticality) params.append('criticality', filters.criticality.join(','));
      if (filters.search) params.append('search', filters.search);
      
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting processes:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch processes',
    };
  }
}

/**
 * Get process by ID
 */
export async function getProcessById(id: string): Promise<ApiResponse<Process>> {
  try {
    const url = buildApiUrl(`/processes?id=${id}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch process',
    };
  }
}

/**
 * Create new process
 */
export async function createProcess(
  process: Omit<Process, 'id' | 'createdAt' | 'updatedAt'>
): Promise<ApiResponse<Process>> {
  try {
    const url = buildApiUrl('/processes');
    const response = await fetch(url, {
      method: 'POST',
      headers: getDefaultHeaders(),
      body: JSON.stringify(process),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create process',
    };
  }
}

/**
 * Update existing process
 */
export async function updateProcess(
  id: string,
  updates: Partial<Process>
): Promise<ApiResponse<Process>> {
  try {
    const url = buildApiUrl(`/processes?id=${id}`);
    const response = await fetch(url, {
      method: 'PUT',
      headers: getDefaultHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update process',
    };
  }
}

/**
 * Delete process
 */
export async function deleteProcess(id: string): Promise<ApiResponse<void>> {
  try {
    const url = buildApiUrl(`/processes?id=${id}`);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting process:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete process',
    };
  }
}
